import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { authReducer } from "features/auth";
import { breadcrumbsReducer } from "features/breadcrumbs";
import { infoReducer } from "features/info";
import { notificationsReducer } from "features/notifications";
import { pageHeaderReducer } from "features/page-header";
import { settingsReducer } from "features/settings";
import { timeReducer } from "features/time";
/* PLOP_IMPORT_REDUCERS */
import { draftWizardReducer } from "features/draft-wizard";
import { activityLogSplitPanelReducer } from "features/activity-log-split-panel";
import { filePreviewSplitPanelReducer } from "components/file-preview-split-panel";
import { dataCreateReducer } from "features/data-create";
import {
  uiContentReducer,
  litigationUiContentReducer,
  noticeUiContentReducer,
} from "features/ui-content";
import { auditsReducer } from "features/audits";
import { gaiaObjectsReducer } from "features/gaiaObjects";
import { teamSettingsReducer } from "features/team-settings";
import { localApi } from "services/local";
import { mainApi } from "services/main";
import { approvalReducer } from "features/approval";
import { atlasChatServiceApi } from "services/atlas-chat";

export const store = configureStore({
  reducer: {
    auth: authReducer,
    breadcrumbs: breadcrumbsReducer,
    info: infoReducer,
    notifications: notificationsReducer,
    pageHeader: pageHeaderReducer,
    settings: settingsReducer,
    time: timeReducer,
    /* PLOP_INJECT_REDUCERS */
    draftWizard: draftWizardReducer,
    activityLogSplitPanel: activityLogSplitPanelReducer,
    filePreviewSplitPanel: filePreviewSplitPanelReducer,
    approval: approvalReducer,
    dataCreate: dataCreateReducer,
    uiContent: uiContentReducer,
    litigationUiContent: litigationUiContentReducer,
    noticeUiContent: noticeUiContentReducer,
    audits: auditsReducer,
    gaiaObjects: gaiaObjectsReducer,
    teamSettings: teamSettingsReducer,
    [localApi.reducerPath]: localApi.reducer,
    [mainApi.reducerPath]: mainApi.reducer,
    [atlasChatServiceApi.reducerPath]: atlasChatServiceApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Note: One of the core usage principles for Redux is that you should not put
      // non-serializable values in state or actions.
      // See: https://redux-toolkit.js.org/usage/usage-guide#working-with-non-serializable-data
      // We turn it off to allow the infoReducer actions to accept non-serializable state value
      // to update the AppLayout tools' inner content with a ReactNode.
      serializableCheck: false,
    })
      .concat(localApi.middleware)
      .concat(mainApi.middleware)
      .concat(atlasChatServiceApi.middleware),
});

setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
